import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Settings, Mail, Cloud, Save, RefreshCw, Send, Plus, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { getAuthHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

function RecipientTag({ email, onRemove }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 text-sm text-zinc-300">
      {email}
      <button onClick={() => onRemove(email)} className="text-zinc-500 hover:text-red-400">
        <X size={14} />
      </button>
    </div>
  );
}

function RecipientList({ emails, onRemove }) {
  if (emails.length === 0) {
    return <p className="text-zinc-500 text-sm">No recipients configured</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {emails.map((email) => <RecipientTag key={email} email={email} onRemove={onRemove} />)}
    </div>
  );
}

function EmailConfigTab() {
  const [config, setConfig] = useState({
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    from_email: "",
    to_list: [],
    enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/email-config`, { headers: getAuthHeaders() })
      .then((res) => setConfig(res.data))
      .catch(() => toast.error("Failed to load email config"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-config`, config, { headers: getAuthHeaders() });
      toast.success("Email configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/admin/email-config/test`, {}, { headers: getAuthHeaders() });
      toast.success("Test email sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  const addRecipient = () => {
    if (newEmail && !config.to_list.includes(newEmail)) {
      setConfig({ ...config, to_list: [...config.to_list, newEmail] });
      setNewEmail("");
    }
  };

  const removeRecipient = (email) => {
    setConfig({ ...config, to_list: config.to_list.filter(e => e !== email) });
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>;

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-900/50 border-white/5">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Mail size={18} /> SMTP Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} className="rounded" />
            Enable Email Notifications
          </label>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">SMTP Host</label>
              <Input value={config.smtp_host} onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })} placeholder="smtp.gmail.com" className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">SMTP Port</label>
              <Input type="number" value={config.smtp_port} onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) || 587 })} placeholder="587" className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">SMTP Username</label>
              <Input value={config.smtp_user} onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })} placeholder="your-email@gmail.com" className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">SMTP Password</label>
              <div className="relative">
                <Input type={showPass ? "text" : "password"} value={config.smtp_password} onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })} placeholder="••••••••" className="bg-zinc-800 border-zinc-700 text-white pr-10" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">From Email</label>
            <Input value={config.from_email} onChange={(e) => setConfig({ ...config, from_email: e.target.value })} placeholder="noreply@company.com" className="bg-zinc-800 border-zinc-700 text-white max-w-md" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/50 border-white/5">
        <CardHeader>
          <CardTitle className="text-lg text-white">Global Recipients</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="recipient@example.com" className="bg-zinc-800 border-zinc-700 text-white max-w-xs" onKeyPress={(e) => e.key === 'Enter' && addRecipient()} />
            <Button onClick={addRecipient} className="bg-blue-600 hover:bg-blue-500"><Plus size={16} /></Button>
          </div>
          <RecipientList emails={config.to_list} onRemove={removeRecipient} />
        </CardContent>
      </Card>

      <div className="flex gap-4">
        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500">
          {saving ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
          Save
        </Button>
        <Button onClick={handleTest} disabled={testing || !config.enabled} variant="outline" className="border-zinc-700 text-zinc-300">
          {testing ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
          Test
        </Button>
      </div>
    </div>
  );
}

function OneDriveConfigTab() {
  const [config, setConfig] = useState({ client_id: "", client_secret: "", tenant_id: "", folder_path: "/qPro_Comparisons", enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/onedrive-config`, { headers: getAuthHeaders() })
      .then((res) => setConfig(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/onedrive-config`, config, { headers: getAuthHeaders() });
      toast.success("OneDrive configuration saved");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>;

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-900/50 border-white/5">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Cloud size={18} /> OneDrive Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} className="rounded" />
            Enable OneDrive Integration
          </label>

          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-amber-400 text-sm">Create an Azure App Registration with Graph API permissions to use OneDrive.</p>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Client ID</label>
            <Input value={config.client_id} onChange={(e) => setConfig({ ...config, client_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="bg-zinc-800 border-zinc-700 text-white" />
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Client Secret</label>
            <div className="relative">
              <Input type={showSecret ? "text" : "password"} value={config.client_secret} onChange={(e) => setConfig({ ...config, client_secret: e.target.value })} placeholder="••••••••" className="bg-zinc-800 border-zinc-700 text-white pr-10" />
              <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Tenant ID</label>
            <Input value={config.tenant_id} onChange={(e) => setConfig({ ...config, tenant_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="bg-zinc-800 border-zinc-700 text-white" />
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Folder Path</label>
            <Input value={config.folder_path} onChange={(e) => setConfig({ ...config, folder_path: e.target.value })} placeholder="/qPro_Comparisons" className="bg-zinc-800 border-zinc-700 text-white max-w-md" />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500">
        {saving ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
        Save
      </Button>
    </div>
  );
}

export default function AdminSettingsPage() {
  const [tab, setTab] = useState("email");

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="text-blue-400" size={28} />
            Admin Settings
          </h1>
          <p className="text-zinc-500 mt-1">Configure email and integrations</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-zinc-900 border border-white/10 mb-6">
            <TabsTrigger value="email" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
              <Mail size={16} className="mr-2" /> Email
            </TabsTrigger>
            <TabsTrigger value="onedrive" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
              <Cloud size={16} className="mr-2" /> OneDrive
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email"><EmailConfigTab /></TabsContent>
          <TabsContent value="onedrive"><OneDriveConfigTab /></TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
}
